import { QuickReviewService } from "../../../../api/services/quickReviewService";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  QuickReviewListItem,
  QuickReviewListRequest,
} from "../../../../data-model/quickReviewModel";
import { QuickReviewOrdersState } from "../hooks/useQuickReviewOrdersTableData";
import {
  QuickReviewTableData,
  QuickReviewTableUtils,
} from "../utils/quickReviewTableUtils";

/**
 * Fetches the next page of Quick Review board rows and appends the results to
 * the existing board state and table data (infinite-scroll pattern).
 *
 * @param payload - List request including the target page number
 * @param authStatusOptions - Available auth status options for badge rendering
 * @param setQuickReviewOrdersState - State setter for the board loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 */
export const makePostCallForNextPagesDataFetch = async (
  payload: QuickReviewListRequest,
  authStatusOptions: AuthStatusOptionModel[],
  setQuickReviewOrdersState: React.Dispatch<
    React.SetStateAction<QuickReviewOrdersState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<QuickReviewTableData[]>>,
) => {
  setQuickReviewOrdersState((prev) => ({
    ...prev,
    showinLineLoader: true,
  }));
  try {
    const response = await QuickReviewService.list(payload);
    setQuickReviewOrdersState((prev) => ({
      ...prev,
      data: {
        ...response,
        items: appendItemsToExistingTableData(
          prev.data?.items ?? [],
          response.items ?? [],
        ),
      },
      totalCount: response.total_count,
    }));
    setTableData((prev) => [
      ...prev,
      ...QuickReviewTableUtils.generateTableDataFromQuickReviewItems(
        response.items ?? [],
        authStatusOptions,
      ),
    ]);
  } catch (error) {
    setQuickReviewOrdersState((prev) => ({
      ...prev,
      error: error instanceof Error ? error.message : String(error),
    }));
  } finally {
    setQuickReviewOrdersState((prev) => ({
      ...prev,
      showinLineLoader: false,
    }));
  }
};

/**
 * Concatenates new board items onto the existing items array.
 *
 * @param existingTableData - Previously fetched board items
 * @param newItems - Newly fetched board items to append
 * @returns Combined array of all items
 */
const appendItemsToExistingTableData = (
  existingTableData: QuickReviewListItem[],
  newItems: QuickReviewListItem[],
) => {
  return existingTableData.concat(newItems);
};
